// ⚡️ MESSAGE PROCESSING: Message processing logic for chat functionality
// Extracted from ChatPanel.js for better organization and reusability

import { useCallback, useRef } from 'react';
import CryptoJS from 'crypto-js';
import { 
  validateInput, 
  logSecurityEvent, 
  MAX_MESSAGE_LENGTH,
  MAX_SEEN,
  getTextContent,
  isNear
} from '../utils/chatUtils';
import { 
  validateMessageType, 
  verifyMessageIntegrity 
} from '../utils/messageValidation';

// Custom hook for message processing
export const useMessageProcessing = () => {
  // ⚡️ PERFORMANCE OPTIMIZATION: Socket message deduplication with size-bounded LRU
  const seenMessageIds = useRef(new Map()); // id -> timestamp

  // ⚡️ PERFORMANCE OPTIMIZATION: Socket message deduplication with size-bounded LRU
  const deduplicateMessage = useCallback((message) => {
    const messageId = message.id || `${message.from}_${message.timestamp}_${message.text}`;
    
    // Check if we've already seen this message
    if (seenMessageIds.current.has(messageId)) {
      // Duplicate message detected
      return false;
    }
    
    // Add to seen messages with current timestamp
    seenMessageIds.current.set(messageId, Date.now());
    
    // ⚡️ MEMORY MANAGEMENT: Clean up oldest entries if we exceed MAX_SEEN
    if (seenMessageIds.current.size > MAX_SEEN) {
      // Delete oldest ~10% of entries to maintain constant memory usage
      const toDelete = Math.floor(MAX_SEEN * 0.1);
      const entries = [...seenMessageIds.current.entries()];
      const sortedEntries = entries.sort((a, b) => a[1] - b[1]); // Sort by timestamp (oldest first)
      const oldestEntries = sortedEntries.slice(0, toDelete);
      
      oldestEntries.forEach(([id]) => {
        seenMessageIds.current.delete(id);
      });
      
      // Cleaned up old message IDs from deduplication cache
    }
    
    return true;
  }, []);

  // ⚡️ UTILITY: Get deduplication cache statistics
  const getDeduplicationStats = useCallback(() => {
    const now = Date.now();
    const entries = [...seenMessageIds.current.entries()];
    const totalEntries = entries.length;
    const oldEntries = entries.filter(([_, timestamp]) => now - timestamp > 5 * 60 * 1000); // Older than 5 minutes
    
    return {
      totalEntries,
      oldEntries: oldEntries.length,
      memoryUsage: `${totalEntries}/${MAX_SEEN} entries`,
      cleanupRecommended: oldEntries.length > 100
    };
  }, []);

  // ⚡️ PERFORMANCE OPTIMIZATION: Extract message processing logic
  const processMessages = useCallback((messages, isAdmin, conv, userEmail) => {
    const filteredMessages = messages.filter(msg =>
      msg && (
        (msg.text && typeof msg.text === 'string' && msg.text.trim() !== '') ||
        (msg.text && typeof msg.text === 'object' && msg.text.sanitized && msg.text.sanitized.trim() !== '') ||
        (msg.type === 'image' && msg.url) ||
        (msg.type === 'video' && msg.url) ||
        (msg.type === 'order' && (
          // Show order messages for admin dashboard
          isAdmin ||
          // Show order messages for seller if they are the seller
          (conv && conv.seller && conv.seller.email === userEmail) ||
          // Show order messages for buyer if they are the buyer
          (conv && conv.buyer && conv.buyer.email === userEmail)
        ))
      )
    );

    // Build a map of the latest order message per order_id
    const latestOrderMsgById = {};
    filteredMessages.forEach(msg => {
      if (msg.type === 'order' && msg.order_id) {
        if (!latestOrderMsgById[msg.order_id] || (msg.timestamp?.seconds || 0) > (latestOrderMsgById[msg.order_id].timestamp?.seconds || 0)) {
          latestOrderMsgById[msg.order_id] = msg;
        }
      }
    });

    // Build a set of order message IDs to keep (latest per order_id)
    const latestOrderMsgIds = new Set(Object.values(latestOrderMsgById).map(msg => msg.id));

    // Merge all messages, but for order messages, only keep the latest per order_id
    const mergedMessages = filteredMessages.filter(msg => {
      if (msg.type === 'order') {
        return latestOrderMsgIds.has(msg.id);
      }
      return true;
    });

    // Sort all messages by timestamp (oldest to newest)
    return mergedMessages.slice().sort((a, b) => {
      const aTime = a.timestamp?.seconds || 0;
      const bTime = b.timestamp?.seconds || 0;
      return aTime - bTime;
    });
  }, []);

  // ⚡️ SECURITY: Socket message validation with Web Worker access
  const validateSocketMessage = useCallback(async (msg, sanitizeText) => {
    if (!msg || typeof msg !== 'object') {
      logSecurityEvent('INVALID_SOCKET_MESSAGE', { message: 'Non-object message received' }, 'warning');
      return null;
    }
    
    // Validate required fields
    if (!msg.from || !msg.text) {
      logSecurityEvent('INVALID_SOCKET_MESSAGE', { message: 'Missing required fields' }, 'warning');
      return null;
    }
    
    // 🛡️ REMOVED CLIENT-SIDE CSRF VALIDATION - Now handled server-side for security
    
    // Security: Validate message type and structure
    if (!validateMessageType(msg)) {
      logSecurityEvent('INVALID_MESSAGE_TYPE', { 
        messageType: msg.type,
        messageId: msg.id 
      }, 'warning');
      return null;
    }
    
    // Security: Verify message integrity with enhanced feedback
    // Sanitize message content first using Web Worker
    const base = {
      ...msg,
      text: (await sanitizeText(msg.text)).sanitized,
      from: (await sanitizeText(msg.from)).sanitized
    };
    
    // Validate message integrity
    const integrityCheck = verifyMessageIntegrity(base);
    const sanitizedMsg = {
      ...base,
      ...(integrityCheck.isValid ? {} : { 
        isTampered: true, 
        tamperReason: integrityCheck.reason 
      })
    };
    
    if (!integrityCheck.isValid) {
      logSecurityEvent('MESSAGE_INTEGRITY_VERIFICATION_FAILED', { 
        messageType: msg.type,
        messageId: msg.id,
        reason: integrityCheck.reason
      }, 'error');
    }
    
    // Validate message length
    if (sanitizedMsg.text.length > MAX_MESSAGE_LENGTH) {
      logSecurityEvent('MESSAGE_TOO_LONG', { length: sanitizedMsg.text.length }, 'warning');
      return null;
    }
    
    // Add message integrity hash
    sanitizedMsg.integrityHash = CryptoJS.SHA256(
      `${sanitizedMsg.text}${sanitizedMsg.from}${sanitizedMsg.timestamp || Date.now()}`
    ).toString();
    
    return sanitizedMsg;
  }, []);

  // Helper function to check if two messages are duplicates
  const isDuplicateMessage = useCallback((existingMsg, newMsg) => {
    const getText = (text) => getTextContent(text);
    return (
      getText(existingMsg.text) === getText(newMsg.text) &&
      existingMsg.from === newMsg.from &&
      (isNear(existingMsg.timestamp ?? existingMsg.createdAt ?? existingMsg.clientCreatedAt, 
              newMsg.timestamp ?? newMsg.createdAt ?? newMsg.clientCreatedAt) ||
       (newMsg.isTelegramReply && existingMsg.isTelegramReply)) // extra nudge for TG replies
    );
  }, []);

  // Helper function to process message in background
  const processMessageInBackground = useCallback(async (instantMessage, options = {}) => {
    try {
      const messageText = instantMessage.text;
      
      // 🛡️ REMOVED CLIENT-SIDE RATE LIMITING - Now handled server-side via API
      
      // Security: Input validation
      const inputValidation = validateInput(messageText);
      if (!inputValidation.isValid) {
        return {
          success: false,
          error: inputValidation.error,
          action: {
            type: 'UPDATE_PENDING_MESSAGE',
            payload: { 
              id: instantMessage.id, 
              status: 'failed',
              error: inputValidation.error
            }
          }
        };
      }
      
      // Security: Enhanced sanitization using Web Worker
      const sanitizedText = (await options.sanitizeText(messageText)).sanitized;
      
      // Security: Log message sending
      logSecurityEvent('MESSAGE_SENT', { 
        conversationId: options.conversation?.id, 
        messageLength: sanitizedText.length,
        isAgent: true 
      });
      
      return {
        success: true,
        sanitizedText,
        messageData: {
          text: sanitizedText,
          from: options.user.email,
          fromUid: options.user.uid || options.user.id || options.user.email,
          timestamp: options.serverTimestamp(),
          status: 'sent',
          readBy: [options.user.email],
          readByAdmin: options.isAdminDashboard === true,
          isAgent: true,
          conversationId: options.convId,
          tempId: instantMessage.id,
          type: 'text'
        }
      };
    } catch (error) {
      logSecurityEvent('MESSAGE_PROCESSING_ERROR', { 
        error: error.message,
        messageId: instantMessage.id 
      }, 'error');
      
      return {
        success: false,
        error: error.message,
        action: {
          type: 'UPDATE_PENDING_MESSAGE',
          payload: { 
            id: instantMessage.id, 
            status: 'failed',
            error: error.message
          }
        }
      };
    }
  }, []);

  return {
    deduplicateMessage,
    getDeduplicationStats,
    processMessages,
    validateSocketMessage,
    isDuplicateMessage,
    processMessageInBackground,
    seenMessageIds
  };
};
